import { earnedChipText, rowDateText } from "./format";
import { ProgressQuery, ProgressRow } from "./types";

const AMOUNT_PATTERN = /^[+-]?\d[\d.,]*$/;
const SIGNED_TERM = /^[+−-]/;

const toCents = (euros: number) => Math.round(euros * 100);

// A separator followed by exactly three digits is a thousands group, so "1.234" is 1234 but "1.23" is 1.23.
export const parseAmountQuery = (value: string): number | null => {
  const text = value
    .trim()
    .replace(/[€\s  ]/g, "")
    .replace(/−/g, "-");
  if (!AMOUNT_PATTERN.test(text)) {
    return null;
  }
  const last = Math.max(text.lastIndexOf(","), text.lastIndexOf("."));
  const hasDecimals = last >= 0 && text.length - last - 1 !== 3;
  const normalized = hasDecimals
    ? `${text.slice(0, last).replace(/[.,]/g, "")}.${text.slice(last + 1)}`
    : text.replace(/[.,]/g, "");
  const amount = Number(normalized);
  return Number.isFinite(amount) ? toCents(amount) : null;
};

export const visibleSearchText = (row: ProgressRow): string => {
  const [year, month, day] = row.date.split("T")[0].split("-");
  const earned =
    row.cashbackEarned === null ? "" : earnedChipText(row.cashbackEarned);
  return [
    rowDateText(row.date),
    `${year}-${month}-${day}`,
    `${day}/${month}/${year}`,
    `${day}-${month}-${year}`,
    row.type,
    row.secondary,
    row.detail,
    row.sub,
    earned,
    row.lockLabel,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
};

const matchesAmount = (row: ProgressRow, term: string): boolean => {
  const wanted = parseAmountQuery(term);
  if (wanted === null) {
    return false;
  }
  const actual = toCents(row.amount);
  if (SIGNED_TERM.test(term)) {
    return wanted === actual;
  }
  return Math.abs(wanted) === Math.abs(actual);
};

export const matchesSearch = (row: ProgressRow, query: string): boolean => {
  const normalized = query.trim().toLowerCase();
  if (!normalized) {
    return true;
  }
  const text = visibleSearchText(row);
  if (parseAmountQuery(normalized) !== null) {
    return matchesAmount(row, normalized) || text.includes(normalized);
  }
  return normalized
    .split(/\s+/)
    .every((term) => text.includes(term) || matchesAmount(row, term));
};

export const filterRows = (
  rows: ProgressRow[],
  { filter, query }: ProgressQuery,
): ProgressRow[] =>
  rows.filter(
    (row) =>
      (filter === "all" || row.groups.includes(filter)) &&
      matchesSearch(row, query),
  );
